import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

/**
 * ClosingWizard - Guided multi-step flow to close an active cycle.
 *
 * Steps:
 *  1. Inputs: company profit (USDT) + conversion rate
 *  2. Review eligible capital per client (read-only)
 *  3. Set custom profit % per client (overrides default)
 *  4. Pay-mode per INR client (INR vs USDT) + cash pool sufficiency check
 *  5. Final review + confirm & close
 *
 * Wraps existing endpoints — no new backend logic.
 *
 * Props:
 *  - cycle:        active cycle object (must have id, name, default_profit_percent)
 *  - clients:      cycleClients array (from /cycles/{id}/clients)
 *  - cashPoolINR:  current INR cash-pool balance (number)
 *  - onClose():    user closed/cancelled wizard
 *  - onClosed():   cycle has been finalized (refresh parent)
 */
export const ClosingWizard = ({ cycle, clients, cashPoolINR = 0, onClose, onClosed }) => {
  const [step, setStep] = useState(1);
  const [companyProfit, setCompanyProfit] = useState('');
  const [conversionRate, setConversionRate] = useState('');
  const [customPercents, setCustomPercents] = useState({}); // client_id -> %
  const [payModes, setPayModes] = useState({}); // INR client_id -> 'INR' | 'USDT'
  const [submitting, setSubmitting] = useState(false);

  // Initialize pay modes default to INR for INR clients, USDT for USDT clients
  useEffect(() => {
    const m = {};
    (clients || []).forEach((c) => {
      m[c.client_id] = c.base_currency === 'INR' ? 'INR' : 'USDT';
      if (c.custom_profit_percent != null) {
        setCustomPercents((prev) => ({ ...prev, [c.client_id]: c.custom_profit_percent }));
      }
    });
    setPayModes(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const profitNum = parseFloat(companyProfit) || 0;
  const rateNum = parseFloat(conversionRate) || 0;

  // Compute projected profit per client using selected % + pay mode
  const projected = useMemo(() => {
    let totalInrCash = 0;
    let totalUsdt = 0;
    let totalUsdtEquivalent = 0;
    const rows = (clients || []).map((c) => {
      const pct = customPercents[c.client_id] != null && customPercents[c.client_id] !== ''
        ? parseFloat(customPercents[c.client_id])
        : (c.custom_profit_percent ?? c.default_profit_percent ?? cycle.default_profit_percent ?? 0);
      const profitBase = (c.eligible_capital_base || 0) * (pct / 100);
      const payMode = payModes[c.client_id] || (c.base_currency === 'INR' ? 'INR' : 'USDT');
      let payAmount = profitBase;
      let payAmountUsdt = 0;
      if (c.base_currency === 'INR') {
        if (payMode === 'INR') {
          totalInrCash += profitBase;
          payAmountUsdt = rateNum > 0 ? profitBase / rateNum : 0;
        } else {
          payAmount = rateNum > 0 ? profitBase / rateNum : 0;
          totalUsdt += payAmount;
          payAmountUsdt = payAmount;
        }
      } else {
        // USDT client
        totalUsdt += profitBase;
        payAmountUsdt = profitBase;
      }
      totalUsdtEquivalent += payAmountUsdt;
      return { ...c, pct, profitBase, payMode, payAmount, payAmountUsdt };
    });
    return { rows, totalInrCash, totalUsdt, totalUsdtEquivalent };
  }, [clients, customPercents, payModes, rateNum, cycle]);

  const cashShortfall = Math.max(0, projected.totalInrCash - (cashPoolINR || 0));
  const retention = profitNum - projected.totalUsdtEquivalent;

  const canGoStep1 = profitNum > 0 && rateNum > 0;
  const canGoStep2 = (clients || []).length > 0;

  // ============ ACTIONS ============

  const saveDraftAndProceed = async () => {
    setSubmitting(true);
    try {
      // Step A: persist any custom profit % overrides
      for (const c of clients || []) {
        const v = customPercents[c.client_id];
        if (v == null || v === '') continue;
        const num = parseFloat(v);
        if (Number.isNaN(num)) continue;
        if (num !== c.custom_profit_percent) {
          await axiosInstance.put(`/cycles/${cycle.id}/custom-profit`, {
            client_id: c.client_id,
            custom_profit_percent: num,
          });
        }
      }
      // Step B: save draft with pay modes
      const client_payouts = (clients || []).map((c) => ({
        client_id: c.client_id,
        payout_currency: payModes[c.client_id] || (c.base_currency === 'INR' ? 'INR' : 'USDT'),
      }));
      await axiosInstance.post(`/cycles/${cycle.id}/save-draft`, {
        company_profit_usdt: profitNum,
        conversion_rate: rateNum,
        client_payouts,
      });
      toast.success('Draft saved');
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSubmitting(false);
    }
  };

  const finalizeClose = async () => {
    if (cashShortfall > 0) {
      const ok = window.confirm(
        `WARNING: Cash pool is short by ₹${cashShortfall.toLocaleString()}.\n\n` +
        `Closing now will create payouts but you won't have enough INR to pay all clients yet. ` +
        `You'll need to top up the cash pool before marking those payouts as Paid.\n\nProceed anyway?`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      await axiosInstance.post(`/cycles/${cycle.id}/close`, {
        total_profit_made_usdt: profitNum,
      });
      toast.success(`Cycle "${cycle.name}" closed successfully`);
      onClosed && onClosed();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close cycle');
    } finally {
      setSubmitting(false);
    }
  };

  // ============ STEP RENDERERS ============

  const StepHeader = ({ n, label }) => (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step === n ? 'bg-indigo-600 text-white' : step > n ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
        {step > n ? <Check className="w-4 h-4" /> : n}
      </div>
      <span className={`text-sm ${step === n ? 'font-semibold text-indigo-700' : step > n ? 'text-green-700' : 'text-gray-500'}`}>{label}</span>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold mb-1">Step 1 · Cycle Inputs</h3>
        <p className="text-xs text-gray-500">Enter the actual profit you made in USDT, and the INR↔USDT conversion rate to use for this cycle.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Company Profit Made (USDT) *</label>
          <input
            type="number"
            step="0.01"
            value={companyProfit}
            onChange={(e) => setCompanyProfit(e.target.value)}
            placeholder="e.g. 25000"
            className="form-input w-full"
            data-testid="wizard-company-profit"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Conversion Rate (INR per 1 USDT) *</label>
          <input
            type="number"
            step="0.01"
            value={conversionRate}
            onChange={(e) => setConversionRate(e.target.value)}
            placeholder="e.g. 94"
            className="form-input w-full"
            data-testid="wizard-conversion-rate"
          />
        </div>
      </div>
      {profitNum > 0 && rateNum > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-sm">
          <span className="font-semibold">Profit in INR equivalent: </span>
          ₹{(profitNum * rateNum).toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold mb-1">Step 2 · Review Eligible Capital</h3>
        <p className="text-xs text-gray-500">Confirm capital per client. Mid-cycle deposits are included unless flagged in the Ledger.</p>
      </div>
      <div className="border rounded max-h-80 overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-left px-3 py-2">Currency</th>
              <th className="text-right px-3 py-2">Capital</th>
              <th className="text-right px-3 py-2">USDT Eq.</th>
            </tr>
          </thead>
          <tbody>
            {(clients || []).map((c) => (
              <tr key={c.client_id} className="border-t">
                <td className="px-3 py-2"><span className="font-medium">{c.client_code}</span> · {c.client_name}</td>
                <td className="px-3 py-2">{c.base_currency}</td>
                <td className="px-3 py-2 text-right">{c.base_currency === 'INR' ? '₹' : ''}{(c.eligible_capital_base || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-gray-600">{(c.eligible_capital_usdt || 0).toLocaleString()}</td>
              </tr>
            ))}
            {(!clients || clients.length === 0) && (
              <tr><td colSpan="4" className="text-center px-3 py-4 text-gray-500">No eligible clients</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold mb-1">Step 3 · Profit Percentage</h3>
        <p className="text-xs text-gray-500">Default is <strong>{cycle.default_profit_percent}%</strong>. Override per-client if needed.</p>
      </div>
      <div className="border rounded max-h-80 overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-right px-3 py-2">Capital</th>
              <th className="text-right px-3 py-2 w-32">Custom %</th>
              <th className="text-right px-3 py-2">Projected Profit</th>
            </tr>
          </thead>
          <tbody>
            {projected.rows.map((c) => (
              <tr key={c.client_id} className="border-t">
                <td className="px-3 py-2"><span className="font-medium">{c.client_code}</span> · {c.client_name}</td>
                <td className="px-3 py-2 text-right text-xs text-gray-500">{c.base_currency === 'INR' ? '₹' : ''}{(c.eligible_capital_base || 0).toLocaleString()}</td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    step="0.01"
                    value={customPercents[c.client_id] ?? ''}
                    onChange={(e) => setCustomPercents({ ...customPercents, [c.client_id]: e.target.value })}
                    placeholder={`${cycle.default_profit_percent}`}
                    className="form-input w-24 text-right text-sm"
                    data-testid={`wizard-pct-${c.client_code}`}
                  />
                </td>
                <td className="px-3 py-2 text-right font-semibold text-green-700">
                  {c.base_currency === 'INR' ? '₹' : ''}{c.profitBase.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const inrClients = projected.rows.filter((c) => c.base_currency === 'INR');
    return (
      <div className="space-y-3">
        <div>
          <h3 className="text-base font-semibold mb-1">Step 4 · Pay Mode for INR Clients</h3>
          <p className="text-xs text-gray-500">USDT clients are always paid in USDT. INR clients can opt-in for USDT.</p>
        </div>
        <div className="border rounded max-h-72 overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-right px-3 py-2">Profit (INR)</th>
                <th className="text-center px-3 py-2 w-44">Pay Mode</th>
                <th className="text-right px-3 py-2">Pays</th>
              </tr>
            </thead>
            <tbody>
              {inrClients.map((c) => (
                <tr key={c.client_id} className="border-t">
                  <td className="px-3 py-2"><span className="font-medium">{c.client_code}</span> · {c.client_name}</td>
                  <td className="px-3 py-2 text-right">₹{c.profitBase.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="px-3 py-2 text-center">
                    <label className="mr-3 text-xs">
                      <input type="radio" name={`pm-${c.client_id}`} checked={c.payMode === 'INR'} onChange={() => setPayModes({ ...payModes, [c.client_id]: 'INR' })} data-testid={`wizard-pm-inr-${c.client_code}`} className="mr-1" />
                      INR
                    </label>
                    <label className="text-xs">
                      <input type="radio" name={`pm-${c.client_id}`} checked={c.payMode === 'USDT'} onChange={() => setPayModes({ ...payModes, [c.client_id]: 'USDT' })} data-testid={`wizard-pm-usdt-${c.client_code}`} className="mr-1" />
                      USDT
                    </label>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {c.payMode === 'INR'
                      ? `₹${c.payAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                      : `${c.payAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT`}
                  </td>
                </tr>
              ))}
              {inrClients.length === 0 && (
                <tr><td colSpan="4" className="text-center px-3 py-4 text-gray-500">No INR clients in this cycle</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={`rounded p-3 text-sm border ${cashShortfall > 0 ? 'bg-orange-50 border-orange-300 text-orange-800' : 'bg-green-50 border-green-300 text-green-800'}`}>
          <div className="flex items-center gap-2">
            {cashShortfall > 0 ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            <span className="font-semibold">Cash Pool Check</span>
          </div>
          <div className="mt-1 text-xs">
            INR cash needed: <strong>₹{projected.totalInrCash.toLocaleString()}</strong> · Available: <strong>₹{(cashPoolINR || 0).toLocaleString()}</strong>
            {cashShortfall > 0 && (
              <span className="block">Shortfall: <strong>₹{cashShortfall.toLocaleString()}</strong> — top up cash pool or switch some clients to USDT pay mode.</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold mb-1">Step 5 · Final Review</h3>
        <p className="text-xs text-gray-500">Confirm everything looks right, then close the cycle.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border rounded p-3 bg-blue-50">
          <p className="text-xs text-blue-700">Profit Made</p>
          <p className="text-lg font-bold text-blue-900">{profitNum.toLocaleString()} USDT</p>
        </div>
        <div className="border rounded p-3 bg-green-50">
          <p className="text-xs text-green-700">To Distribute</p>
          <p className="text-lg font-bold text-green-900">{projected.totalUsdtEquivalent.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT</p>
        </div>
        <div className={`border rounded p-3 ${retention >= 0 ? 'bg-purple-50' : 'bg-red-50'}`}>
          <p className={`text-xs ${retention >= 0 ? 'text-purple-700' : 'text-red-700'}`}>Company Retention</p>
          <p className={`text-lg font-bold ${retention >= 0 ? 'text-purple-900' : 'text-red-900'}`}>{retention.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT</p>
        </div>
        <div className={`border rounded p-3 ${cashShortfall > 0 ? 'bg-orange-50' : 'bg-gray-50'}`}>
          <p className="text-xs text-gray-700">Cash Pool After</p>
          <p className={`text-lg font-bold ${cashShortfall > 0 ? 'text-orange-900' : 'text-gray-900'}`}>
            ₹{((cashPoolINR || 0) - projected.totalInrCash).toLocaleString()}
          </p>
        </div>
      </div>
      <div className="text-xs text-gray-600">
        INR cash payouts: ₹{projected.totalInrCash.toLocaleString()} · USDT payouts: {projected.totalUsdt.toLocaleString(undefined, { maximumFractionDigits: 2 })} USDT · Conversion rate: ₹{rateNum}/USDT
      </div>
      {cashShortfall > 0 && (
        <div className="bg-orange-50 border border-orange-300 rounded p-3 text-sm text-orange-800 flex gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Cash pool short by ₹{cashShortfall.toLocaleString()}.</strong>{' '}
            You can still close the cycle, but you'll need to top-up cash pool before marking those INR payouts as Paid.
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <div>
            <h2 className="text-lg font-bold">Close Cycle: {cycle.name}</h2>
            <p className="text-xs text-gray-500">Guided step-by-step closing</p>
          </div>
          <button onClick={onClose} data-testid="wizard-close-btn" className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex justify-between px-6 py-3 border-b bg-gray-50 overflow-x-auto">
          <StepHeader n={1} label="Inputs" />
          <StepHeader n={2} label="Capital" />
          <StepHeader n={3} label="Profit %" />
          <StepHeader n={4} label="Pay Mode" />
          <StepHeader n={5} label="Confirm" />
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
        </div>

        {/* Footer */}
        <div className="flex justify-between p-5 border-t bg-white">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            data-testid="wizard-prev-btn"
            className="text-gray-600 hover:text-gray-900 flex items-center gap-1 text-sm"
            disabled={submitting}
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 && (
            <button
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !canGoStep1) || (step === 2 && !canGoStep2)}
              data-testid="wizard-next-btn"
              className="btn-primary text-sm flex items-center gap-1"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {step === 4 && (
            <button
              onClick={saveDraftAndProceed}
              disabled={submitting}
              data-testid="wizard-save-draft-btn"
              className="btn-primary text-sm flex items-center gap-1"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              Save Draft & Review
            </button>
          )}
          {step === 5 && (
            <button
              onClick={finalizeClose}
              disabled={submitting}
              data-testid="wizard-finalize-btn"
              className={`text-sm flex items-center gap-1 px-4 py-2 rounded font-semibold text-white ${cashShortfall > 0 ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {cashShortfall > 0 ? 'Confirm & Close (Acknowledge Shortfall)' : 'Confirm & Close Cycle'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
